import {pipe, pick, omit, merge, evolve, prop} from 'ramda';
import {
	buildingsFiltersKeys,
	csvUniqIdStorageKey,
	emptyBuildingsFilters,
	massSelectionLimit,
} from './constants';
import {getJson} from 'io/localStorage';
import {isAfter} from 'date-fns';

const TYPE_HOUSE = 'house';
const TYPE_PROJECT = 'project';
const TYPE_CONDO = 'condo';

export const buildingSpecificTypesByType = {
	[TYPE_HOUSE]: ['detached', 'semidetached', 'otherdetached', 'unknown', 'vacationhome'],
	[TYPE_PROJECT]: [
		'apartmenthouse',
		'other',
		'rowhouse',
		'balconyaccessblock',
		'terracedhouse',
		'detachedhousecompany',
	],
	[TYPE_CONDO]: [
		'apartmenthouse',
		'other',
		'rowhouse',
		'balconyaccessblock',
		'terracedhouse',
		'detachedhousecompany',
	],
};

export const getBuildingsType = (type, overridableTypes = []) => {
	let q = '';
	const types = (
		overridableTypes && overridableTypes.length
			? overridableTypes
			: buildingSpecificTypesByType[type]
	).join(',');

	if (type === TYPE_HOUSE) {
		q = {
			types,
			onlyWithProjectBuildingId: false,
		};
	} else if (type === TYPE_PROJECT) {
		q = {
			types,
			onlyWithProjectBuildingId: false,
		};
	} else if (type === TYPE_CONDO) {
		q = {
			types,
			onlyWithProjectBuildingId: true,
		};
	}

	return q;
};

// PERM: Currently not used, old buildings app had MassEditor exception for this. Probably legacy.
export const isContactUpdater = user =>
	!!user.roles.find(r => r.slug === 'kontaktinpaeivitys');

/**
 * Removes uniqId from filters if it has expired
 * @param {Object} filters
 */

export const checkCsvUniqIdExpiration = filters => {
	const expiresAt = prop('expiresAt', getJson(csvUniqIdStorageKey));

	if (expiresAt) {
		const expiresAtDate = new Date(expiresAt);
		const shouldDropValue = isAfter(new Date(), expiresAtDate);
		if (shouldDropValue) {
			return omit(['uniqId'], filters);
		}
	}
	return filters;
};

export const formatBuildingsFiltersInput = ({buildingsQuery}) => {
	const urlQueryVals = pick(buildingsFiltersKeys, buildingsQuery);

	const filters = {
		...emptyBuildingsFilters,
		...urlQueryVals,
		types: urlQueryVals.types ? urlQueryVals.types.split(',') : [],
		latestEncounterStateOrganizationId: urlQueryVals.latestEncounterStateOrganizationId
			? parseInt(urlQueryVals.latestEncounterStateOrganizationId)
			: null,
	};
	return checkCsvUniqIdExpiration(filters);
};

export const formatMassSelectionQuery = buildingsQuery => {
	const massSelectVals = {
		_limit: massSelectionLimit,
		onlyIds: true,
		_select: 'id',
	};
	const selection = pipe(
		omit(['_limit', '_page', '_sortDirection', '_sortColumn']),
		merge(massSelectVals),
	)(buildingsQuery);

	return selection;
};

export const parseUrlQuery = query => {
	return evolve(
		{
			areas: str => (str ? JSON.parse(str) : []),
			products: str => (str ? JSON.parse(str) : []),
		},
		query,
	);
};

export const formatUrlQuery = ({buildingsQuery}) => {
	const q = {
		...buildingsQuery,
		...(buildingsQuery.areas ? {areas: JSON.stringify(buildingsQuery.areas)} : {}),
		...(buildingsQuery.products
			? {
					products: JSON.stringify(
						buildingsQuery.products.map(p => ({id: p.id, title: p.title})),
					),
			  }
			: {}),
	};
	return checkCsvUniqIdExpiration(q);
};

export const formatFetchableBuildingsQuery = buildingsQuery => {
	const formatAreas = query =>
		query.areas && query.areas.length > 0
			? {
					...query,
					areas: query.areas.map(a => a.id).join(','),
			  }
			: query;

	const formatProducts = query =>
		query.products && query.products.length > 0
			? {
					...query,
					productIds: query.products.map(p => p.id),
			  }
			: query;

	// State contains empty form defaults; the previous URL-based requests omitted them.
	const query = Object.entries(buildingsQuery).reduce((result, [key, value]) => {
		if (value === false && (key === 'hasClients' || key === 'hasContacts')) {
			return {...result, [key]: 'false'};
		}
		if (
			value == null ||
			value === false ||
			(typeof value === 'string' && !value.trim()) ||
			(Array.isArray(value) && value.length === 0)
		)
			return result;
		return {...result, [key]: value};
	}, {});
	return pipe(formatAreas, formatProducts)(query);
};

// Only actual filters count; list controls and the default building group do not.
export const hasBuildingsSearchCriteria = (query, type) => {
	const filters = checkCsvUniqIdExpiration(query || {});
	const selectedTypes = (
		Array.isArray(filters.types) ? filters.types : (filters.types || '').split(',')
	)
		.map(value => value.trim())
		.filter(Boolean);
	const defaultTypes = buildingSpecificTypesByType[type] || [];
	if (
		selectedTypes.some(value => defaultTypes.includes(value)) &&
		defaultTypes.some(value => !selectedTypes.includes(value))
	) {
		return true;
	}
	return buildingsFiltersKeys
		.filter(key => key !== 'types')
		.some(key => {
			const value = filters[key];
			if (key === 'hasClients' || key === 'hasContacts') {
				return value === true || value === false || value === 'true' || value === 'false';
			}
			if (Array.isArray(value)) return value.length > 0;
			if (typeof value === 'string') return value.trim() !== '' && value !== 'false';
			return value !== undefined && value !== null && value !== false;
		});
};
